import React, { useState, useEffect } from "react";
import {
  Typography,
  Grid,
  Paper,
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Chip,
  IconButton,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  CloudUpload as UploadIcon,
  Route as RouteIcon,
  Visibility as ViewIcon,
  Person as ProfileIcon,
  Refresh as RefreshIcon,
  Assignment as OrderIcon,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { routeService } from "../../services/routeService";
import { orderService } from "../../services/orderService";
import analyticsService from "../../services/analyticsService";
import EFaxUploadModal from "../../components/EFaxUploadModal";
import { useAuth } from "../../contexts/AuthContext";

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total_orders: 0,
    created_orders: 0,
    assigned_orders: 0,
    in_progress_orders: 0,
    completed_orders: 0,
    cancelled_orders: 0,
    today_orders: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [activeRoutes, setActiveRoutes] = useState([]);
  const navigate = useNavigate();
  const [efaxModalOpen, setEfaxModalOpen] = useState(false);

  // ADDED: Get user from auth context
  const { user } = useAuth();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use analytics dashboard endpoint instead of orders
      try {

        // Call the analytics dashboard endpoint directly
        const response = await fetch("/api/v1/analytics/dashboard", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            // Add auth headers if needed
          },
        });

        if (!response.ok) {
          throw new Error(`Analytics API responded with ${response.status}`);
        }

        const analyticsData = await response.json();

        // Extract stats from analytics widgets
        const extractWidgetValue = (title) => {
          const widget = analyticsData.widgets?.find((w) =>
            w.title.toLowerCase().includes(title.toLowerCase())
          );
          return widget
            ? typeof widget.value === "string"
              ? parseInt(widget.value.replace(/[^\d]/g, ""))
              : widget.value
            : 0;
        };

        const dashboardStats = {
          total_orders: extractWidgetValue("Total Orders"),
          today_orders: extractWidgetValue("Today"),
          created_orders: 0, // We'll calculate this from the fallback
          assigned_orders: 0,
          in_progress_orders: 0,
          completed_orders: 0,
          cancelled_orders: 0,
        };

        setStats(dashboardStats);
      } catch (analyticsError) {
        console.error("Analytics dashboard endpoint failed:", analyticsError);

        // Fallback: Get basic analytics summary
        try {
          const summaryResponse = await fetch(
            "/api/v1/analytics/summary?time_range=1month",
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          if (summaryResponse.ok) {
            const summaryData = await summaryResponse.json();

            const dashboardStats = {
              total_orders: summaryData.summary?.total_orders || 0,
              today_orders: 0, // Calculate from orders if needed
              created_orders:
                summaryData.order_status_distribution?.created || 0,
              assigned_orders:
                summaryData.order_status_distribution?.assigned || 0,
              in_progress_orders:
                summaryData.order_status_distribution?.in_progress || 0,
              completed_orders:
                summaryData.order_status_distribution?.completed || 0,
              cancelled_orders:
                summaryData.order_status_distribution?.cancelled || 0,
            };

            setStats(dashboardStats);
          } else {
            throw new Error("Analytics summary also failed");
          }
        } catch (summaryError) {
          console.error(
            "Analytics summary failed, using manual calculation:",
            summaryError
          );

          // Final fallback: Manual calculation from orders
          const allOrders = await orderService.getAllOrders({ limit: 1000 });
          const orders = allOrders || [];

          const manualStats = {
            total_orders: orders.length,
            created_orders: orders.filter((o) => o.status === "created").length,
            assigned_orders: orders.filter((o) => o.status === "assigned")
              .length,
            in_progress_orders: orders.filter((o) => o.status === "in_progress")
              .length,
            completed_orders: orders.filter((o) => o.status === "completed")
              .length,
            cancelled_orders: orders.filter((o) => o.status === "cancelled")
              .length,
            today_orders: orders.filter((o) => {
              const today = new Date().toISOString().split("T")[0];
              const orderDate = new Date(o.created_at)
                .toISOString()
                .split("T")[0];
              return orderDate === today;
            }).length,
          };

          setStats(manualStats);
        }
      }

      // Fetch recent orders (this can stay as is since it's just for display)
      try {
        const ordersResponse = await orderService.getAllOrders({ limit: 5 });
        setRecentOrders(ordersResponse || []);
      } catch (ordersError) {
        console.error("Error fetching recent orders:", ordersError);
        setRecentOrders([]);
      }

      // Fetch active routes
      try {
        const routesResponse = await routeService.getAllRoutes({ limit: 5 });
        const routes = routesResponse || [];
        const activeRoutesFiltered = routes.filter(
          (r) => r.status !== "completed" && r.status !== "cancelled"
        );
        setActiveRoutes(activeRoutesFiltered);
      } catch (routesError) {
        console.error("Error fetching routes:", routesError);
        setActiveRoutes([]);
      }
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Failed to load dashboard data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    let color = "default";
    let displayStatus = status;

    switch (status) {
      case "completed":
        color = "success";
        displayStatus = "Completed";
        break;
      case "in_progress":
        color = "primary";
        displayStatus = "In Progress";
        break;
      case "assigned":
        color = "info";
        displayStatus = "Assigned";
        break;
      case "created":
        color = "warning";
        displayStatus = "Created";
        break;
      case "cancelled":
        color = "error";
        displayStatus = "Cancelled";
        break;
      default:
        color = "default";
        displayStatus = status
          ? status.charAt(0).toUpperCase() + status.slice(1)
          : "Unknown";
    }

    return <Chip label={displayStatus} color={color} size="small" />;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Dashboard</Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchDashboardData}
          variant="outlined"
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid  size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper elevation={3} sx={{ p: 3, height: "100%" }}>
            <Typography color="textSecondary" variant="subtitle2" gutterBottom>
              Total Orders
            </Typography>
            <Typography variant="h4">{stats.total_orders || 0}</Typography>
          </Paper>
        </Grid>
        <Grid  size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper elevation={3} sx={{ p: 3, height: "100%" }}>
            <Typography color="textSecondary" variant="subtitle2" gutterBottom>
              Pending Orders
            </Typography>
            <Typography variant="h4" color="warning.main">
              {(stats.created_orders || 0) + (stats.assigned_orders || 0)}
            </Typography>
          </Paper>
        </Grid>
        <Grid  size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper elevation={3} sx={{ p: 3, height: "100%" }}>
            <Typography color="textSecondary" variant="subtitle2" gutterBottom>
              Completed Orders
            </Typography>
            <Typography variant="h4" color="success.main">
              {stats.completed_orders || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid  size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper elevation={3} sx={{ p: 3, height: "100%" }}>
            <Typography color="textSecondary" variant="subtitle2" gutterBottom>
              Today's Orders
            </Typography>
            <Typography variant="h4" color="primary.main">
              {stats.today_orders || 0}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      {(user?.role === "super_admin" ||
        user?.role === "admin" ||
        user?.role === "facility_admin") && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid  size={{ xs: 12, md: 6 }}>
            <Card elevation={3}>
              <CardHeader title="Quick Actions" />
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid  size={{ xs: 6 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<UploadIcon />}
                      onClick={() => setEfaxModalOpen(true)}
                    >
                      Upload eFax
                    </Button>
                  </Grid>
                  <Grid  size={{ xs: 6 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      color="secondary"
                      startIcon={<RouteIcon />}
                      onClick={() => navigate("/routes/optimize")}
                    >
                      Optimize Routes
                    </Button>
                  </Grid>
                  <Grid  size={{ xs: 6 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<AddIcon />}
                      onClick={() => navigate("/orders/create")}
                    >
                      Create Order
                    </Button>
                  </Grid>
                  <Grid  size={{ xs: 6 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      color="secondary"
                      startIcon={<ProfileIcon />}
                      onClick={() => navigate("/patients")}
                    >
                      Manage Patients
                    </Button>
                  </Grid>
                  <Grid  size={{ xs: 6 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      color="secondary"
                      onClick={() => navigate("/reports")}
                    >
                      View Reports
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      {user?.role === "medical_professional" && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid  size={{ xs: 12 }}>
            <Card elevation={3}>
              <CardHeader title="Your Dashboard" />
              <Divider />
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Welcome, {user?.full_name || "Medical Professional"}
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Use the Orders section to view and manage your assigned
                  orders.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate("/orders")}
                  startIcon={<OrderIcon />}
                >
                  View My Orders
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Recent Activity */}
      <Grid container spacing={3}>
        <Grid  size={{ xs: 12, md: 4 }}>
          <Card elevation={3}>
            <CardHeader
              title="Recent Orders"
              action={
                <Button size="small" onClick={() => navigate("/orders")}>
                  View All
                </Button>
              }
            />
            <Divider />
            <List>
              {recentOrders.length > 0 ? (
                recentOrders.map((order) => (
                  <React.Fragment key={order.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => navigate(`/orders/${order.id}`)}
                        >
                          <ViewIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={order.order_number || `Order #${order.id}`}
                        secondary={
                          <>
                            <span>
                              {order.patient
                                ? `${order.patient.first_name || ""} ${
                                    order.patient.last_name || ""
                                  }`.trim() || "Unknown Patient"
                                : "Unknown Patient"}{" "}
                              -{" "}
                              {order.created_at
                                ? new Date(
                                    order.created_at
                                  ).toLocaleDateString()
                                : "Unknown Date"}
                            </span>
                            <Box mt={0.5}>{getStatusChip(order.status)}</Box>
                          </>
                        }
                        secondaryTypographyProps={{ component: "div" }}
                      />
                    </ListItem>
                    {recentOrders.indexOf(order) < recentOrders.length - 1 && (
                      <Divider />
                    )}
                  </React.Fragment>
                ))
              ) : (
                <ListItem>
                  <ListItemText
                    primary="No recent orders"
                    secondary="Add new orders to see them here"
                  />
                </ListItem>
              )}
            </List>
          </Card>
        </Grid>

        <Grid  size={{ xs: 12, md: 4 }}>
          <Card elevation={3}>
            <CardHeader
              title="Active Routes"
              action={
                <Button size="small" onClick={() => navigate("/routes")}>
                  View All
                </Button>
              }
            />
            <Divider />
            <List>
              {activeRoutes.length > 0 ? (
                activeRoutes.map((route) => (
                  <React.Fragment key={route.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => navigate(`/routes/${route.id}`)}
                        >
                          <ViewIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={route.name || `Route #${route.id}`}
                        secondary={
                          <>
                            <span>
                              {route.scheduled_date
                                ? new Date(
                                    route.scheduled_date
                                  ).toLocaleDateString()
                                : "No Date"}
                            </span>
                            <Box mt={0.5}>{getStatusChip(route.status)}</Box>
                          </>
                        }
                        secondaryTypographyProps={{ component: "div" }}
                      />
                    </ListItem>
                    {activeRoutes.indexOf(route) < activeRoutes.length - 1 && (
                      <Divider />
                    )}
                  </React.Fragment>
                ))
              ) : (
                <ListItem>
                  <ListItemText
                    primary="No active routes"
                    secondary="Optimize routes to see them here"
                  />
                </ListItem>
              )}
            </List>
          </Card>
        </Grid>
        <Grid  size={{ xs: 12, md: 4 }}>
          <Card elevation={3}>
            <CardHeader title="System Notifications" />
            <Divider />
            <CardContent>
              <List disablePadding>
                {stats.created_orders + stats.assigned_orders > 0 && (
                  <ListItem>
                    <ListItemText
                      primary={`${
                        stats.created_orders + stats.assigned_orders
                      } Orders Pending`}
                      secondary="These orders need to be processed"
                    />
                    <Button size="small" onClick={() => navigate("/orders")}>
                      View
                    </Button>
                  </ListItem>
                )}
                {activeRoutes.length > 0 && (
                  <ListItem>
                    <ListItemText
                      primary={`${activeRoutes.length} Active Routes`}
                      secondary="Routes that are currently in progress"
                    />
                    <Button size="small" onClick={() => navigate("/routes")}>
                      View
                    </Button>
                  </ListItem>
                )}
                {stats.created_orders + stats.assigned_orders === 0 &&
                  activeRoutes.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary="No pending notifications"
                        secondary="All orders and routes are up to date"
                      />
                    </ListItem>
                  )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <EFaxUploadModal
        open={efaxModalOpen}
        onClose={() => setEfaxModalOpen(false)}
      />
    </Box>
  );
};

export default Dashboard;
